import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SecureMessageIcon from '@icons/secure-message.svg';
import ArrowDown from '@icons/arrow-down.svg';
import classNames from 'classnames';
import { Toast } from '@ds';
import { IShowToast, IToastr } from '@ds/elements/Toast/Toast.types';
import { useSidebarStore } from '../../stores/SidebarStore';
import ChangePasswordModal from '../ChangePasswordModal/ChangePasswordModal';
import { useHeaderStore } from 'src/stores/HeaderStore';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useAuthActions } from 'src/utils/hooks/useAuthActions';
import UploadProfilePictureModal from '../UploadProfilePictureModal/UploadProfilePictureModal';
import ProfileImage from '../ProfileImage/ProfileImage';
import styles from './Header.module.scss';
import RolesSelector from '../RolesSelector/RolesSelector';
import { useGetUnreadSecureMessageCount } from 'src/shared/queries/useGetUnreadSecureMessageCount';
import { PassportClickInterceptor } from '../PassportClickInterceptor/PassportClickInterceptor';
import { useThemeStore } from 'src/stores/ThemeStore';
import DeloitteLogo from '@icons/deloitte/bal-client-portal-logo.svg';
import BalLogo from '@icons/bal-client-portal-logo.svg';

const Header = () => {
  const { logout } = useAuthActions();
  const { data: userInfo } = useGetUser();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isMobileMenuOpen, toggleMobileMenu } = useSidebarStore();
  const { isDeloitteTheme } = useThemeStore();
  const { openSwitchRolesModal } = useHeaderStore();
  const { openChangePasswordModal, openUploadProfilePictureModal } = useHeaderStore();
  const { data: threads } = useGetUnreadSecureMessageCount();
  const navigate = useNavigate();

  // json result is in the form [{caseId:0, count:1}, {caseId:1, count:2}, {caseId: 3, count:1}, ...]
  // The following will sum the counts from all objects in the array
  const unreadSecureMessageCount = Array.isArray(threads)
    ? threads.reduce((total, thread) => total + (thread.count || 0), 0)
    : 0;
  const displayRolesSelectorItem = (userInfo?.linkedUsers?.length ?? -1) > 0;

  const dropdownRef = useRef<HTMLDivElement>(null);
  const userInfoRef = useRef<HTMLDivElement>(null);
  const toastrRef = useRef<IToastr>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        userInfoRef.current &&
        !userInfoRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef, userInfoRef, setIsDropdownOpen]);

  useEffect(() => {
    if ((userInfo?.linkedUsers?.length ?? 0) > 0 && !(sessionStorage.getItem('fnUserRedirected') === 'true')) {
      openSwitchRolesModal();
    }
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const goToSecureMessage = () => {
    window.sessionStorage.clear();

    const IMS_MESSAGES_URL = '/#/dashboard/messages';
    window.location.href = window.FN_CONFIG.IMS_UI_URL + IMS_MESSAGES_URL + '?fnRedirect=true';
  };

  const triggerChangePasswordModal = () => {
    openChangePasswordModal();
    setIsDropdownOpen(false);
  };

  const onDisplayUploadProfilePictureToast = (details: IShowToast) => {
    toastrRef.current?.show(details);
  };

  return (
    <header className={styles.header} id="header">
      <PassportClickInterceptor>
        <div className="flex items-center">
          <div
            className={classNames('mobile-only', {
              [styles['hamburger']]: true,
              [styles['hamburger-open']]: isMobileMenuOpen,
            })}
            onClick={() => toggleMobileMenu()}
          ></div>
          <div
            onClick={() => navigate('/home')}
            className={classNames(styles['client-portal-logo'], {
              [styles['deloitte-client-portal-logo']]: isDeloitteTheme,
            })}
          >
            <img src={isDeloitteTheme ? DeloitteLogo : BalLogo} />
          </div>
        </div>
      </PassportClickInterceptor>
      <PassportClickInterceptor>
        <div className={styles['right-section']}>
          <div className={styles['secure-message-container']}>
            <img className={styles['secure-message-icon']} src={SecureMessageIcon} onClick={goToSecureMessage} />
            {unreadSecureMessageCount > 0 && (
              <div className={styles['secure-message-count-background']}>{unreadSecureMessageCount}</div>
            )}
          </div>
          <div ref={userInfoRef} className={styles['user-info']} onClick={toggleDropdown}>
            <ProfileImage />
            <img className={styles[isDropdownOpen ? 'arrow-up' : 'arrow-down']} src={ArrowDown} />
          </div>
          {isDropdownOpen && (
            <div ref={dropdownRef} className={`${styles['user-dropdown']} tablet-and-desktop`}>
              <div className={styles['top-section']}>
                <ProfileImage />
                <span className={styles['welcome-text']}>Welcome, {userInfo?.fullName}</span>
              </div>
              {[
                { label: 'My Roles', action: openSwitchRolesModal, visible: displayRolesSelectorItem },
                { label: 'My Preferences', action: () => navigate('/user-preferences'), visible: true },
                { label: 'Change Password', action: triggerChangePasswordModal, visible: true },
                { label: 'Change Profile Picture', action: openUploadProfilePictureModal, visible: true },
                { label: 'Logout', action: logout, visible: true },
              ].map((item, index) =>
                item.visible ? (
                  <div
                    className={styles['dropdown-item']}
                    key={index}
                    onClick={() => {
                      if (item.action) {
                        item.action();
                      }
                      setIsDropdownOpen(false);
                    }}
                  >
                    {item.label}
                  </div>
                ) : null
              )}
            </div>
          )}
        </div>
      </PassportClickInterceptor>
      <ChangePasswordModal />
      <RolesSelector />
      <UploadProfilePictureModal displayToast={onDisplayUploadProfilePictureToast} />
      <Toast ref={toastrRef} />
    </header>
  );
};

export default Header;
